import React from "react"
import { useParams, useRouteLoaderData } from "react-router-dom"

import {
  Box,
  Flex,
  Button,
  Alert,
  Text,
  HStack,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  CircularProgress,
} from "@chakra-ui/react"
import { DataTable } from "./DataTable"
import UploadRecords from "./UploadRecords"
import {
  HashedSchemas,
  Schemas,
  UploadProgress,
  FileName,
  IsUploading,
  UploadStatus,
  Limit,
  Offset,
  CurrentFilters,
  SchemaRecordCounts,
} from "../lib/store"
import useFileStore from "../lib/fileStore"

import { Api } from "../lib/api"
import { isEmpty, unrollError } from "../lib/util"
import { FaUpload } from "react-icons/fa6"
import IconLock from "@/icons/IconLock"
import { addAlert, AlertType, removeAlertBySource } from "@/lib/alerts"
import TableFilter from "./TableFilter"

export default function SchemaRecordsList() {
  const [limit] = Limit.useState()
  const [offset] = Offset.useState()
  const [isLoading, setIsLoading] = React.useState(false)
  const user = useRouteLoaderData("auth")
  const { schemaId } = useParams()
  const api = new Api()

  // Initialize state-pool values
  const [schemas] = Schemas.useState()
  const [currentFilters] = CurrentFilters.useState()
  const [hashedSchemas] = HashedSchemas.useState()
  const [uploadProgress, setUploadProgress] = UploadProgress.useState()
  const [, setFileName] = FileName.useState()
  const [isUploading, setIsUploading] = IsUploading.useState()
  const clearSelectedFile = useFileStore((state) => state.clearSelectedFile)
  // List of current records in view
  const [records, setRecords] = React.useState([])

  // Find the selected schema
  const schema = React.useMemo(() => schemas[schemaId], [schemas, schemaId])
  // Get the hashed (decrypted) schema
  const hashedSchema = React.useMemo(
    () => hashedSchemas[schemaId],
    [hashedSchemas, schemaId],
  )

  // Decryption toggle
  const [encrypted, setEncrypted] = React.useState(true)
  // Can decrypt schema?
  const [canDecryptSchema, setCanDecryptSchema] = React.useState(true)

  const isDataOwner = React.useMemo(() => {
    return !user?.email?.startsWith("data_requester")
  }, [user])

  // Can decrypt records?
  const [canDecryptRecords, setCanDecryptRecords] = React.useState(() => {
    // TODO(jathan): This is a temporary hack to disallow decryption for data_requester for now.
    if (!isDataOwner) return false
    // TODO(shakefu): Get decrypt permission state from the API when we
    // support that metadata call
    // Default to allowing decryption, once
    return true
  })
  // Records count
  const count = React.useMemo(() => {
    if (!records?.count) {
      return -1
    }
    return records.count
  }, [records])

  // Records to decrypt
  const [missingRecords, setMissingRecords] = React.useState({})
  // Decrypted records
  const [decryptedRecords, setDecryptedRecords] = React.useState({})
  // Records pending decryption
  const [pendingRecords, setPendingRecords] = React.useState({})
  // Schema pending decryption
  const [pendingSchema, setPendingSchema] = React.useState(false)

  const properties = React.useMemo(
    () => schema?.schema?.properties || {},
    [schema],
  )

  const recordMap = React.useMemo(() => {
    if (pendingSchema) {
      console.debug("recordMap waiting for schema...")
      return
    }
    console.debug("Creating recordMap...")
    let inverted = {}
    for (const record of records) {
      inverted[record.id] = record.data
    }
    if (encrypted) {
      console.debug("Returning encrypted recordMap", inverted)
      return inverted
    }

    let changed = false
    let missing = {}
    for (const id in missingRecords) {
      missing[id] = missingRecords[id]
    }
    for (const record of records) {
      if (!decryptedRecords[record.id]) {
        if (pendingRecords[record.id]) continue
        if (!missing[record.id]) {
          changed = true
          missing[record.id] = record
        }
      }
    }
    if (changed) {
      console.debug("Need to decrypt missing records", missing)
      setMissingRecords(missing)
    }

    console.debug("Returning decryptedRecords", decryptedRecords)
    return decryptedRecords
  }, [
    records,
    decryptedRecords,
    missingRecords,
    pendingRecords,
    pendingSchema,
    encrypted,
  ])

  // This effect will attempt to decrypt the schema if it's not already decrypted.
  // If it fails, it won't retry.
  React.useEffect(() => {
    if (!schema) return
    // If we're not decrypting, get out early
    if (!canDecryptSchema) return
    if (pendingSchema) return
    if (hashedSchemas[schema.id]) return
    setPendingSchema(true)
    console.debug("Decrypting schema for SchemaRecordsList", schema.id)
    api
      .schemasDecrypt(schema)
      .then(() => {
        setPendingSchema(false)
      })
      .catch(() => {
        setPendingSchema(false)
        setCanDecryptSchema(false)
      })
  }, [
    hashedSchemas,
    schema,
    pendingSchema,
    setPendingSchema,
    canDecryptSchema,
    setCanDecryptSchema,
  ])

  const [hasStatus, setHasStatus] = React.useState(false)

  React.useEffect(() => {
    if (!records) return
    const getStatus = async () => {
      return await api.statusRetrieve()
    }
    const resp = getStatus()
    resp.then((resp) => {
      setHasStatus(resp)
    })
  }, [records])

  const hasProxy = React.useMemo(() => {
    if (!hasStatus) return
    return hasStatus.behind_proxy
  }, [hasStatus])

  // This effect will attempt to decrypt the records if "encrypted" is false.
  // If it fails, it should revert to displaying the encrypted records.
  React.useEffect(() => {
    // If we're not decrypting, get out early
    if (encrypted) return

    // If we aren't allowed to decrypt, get out early
    if (!canDecryptRecords) return

    // Calculate actual missing records vs what is pending in an API query
    let missing = []
    if (Object.keys(missingRecords).length > 0) {
      console.debug("Merging missing and pending records", {
        missingRecords,
        pendingRecords,
      })
      let pending = {}
      for (const id in missingRecords) {
        if (!pendingRecords[id]) {
          missing.push(missingRecords[id])
          pending[id] = true
        }
      }
      for (const id in pendingRecords) {
        pending[id] = true
      }

      // If we didn't find any changes, get out
      if (missing.length === 0) return

      // Clear missing records that were set
      console.debug("Clearing missing records", missing)
      setMissingRecords({})

      // Update the pending record state
      console.debug("Updating pending records")
      setPendingRecords(pending)
    }

    console.debug("Missing records", missing)

    // If we're already pending decryption, get out early
    if (missing.length === 0) return

    api
      .recordsDecrypt(missing)
      .then((data) => {
        console.debug("Raw decrypted data", data)
        let newDecrypted = {}
        for (const record of data) {
          newDecrypted[record.id] = record.data
        }
        for (const id in decryptedRecords) {
          newDecrypted[id] = decryptedRecords[id]
        }
        console.debug("Updating decrypted records", decryptedRecords)
        setDecryptedRecords(newDecrypted)
      })
      .catch((error) => {
        console.error("Error decrypting records", error)
        setMissingRecords({})
        if (error?.status === 404) {
          console.error("Decrypt records not found")
          addAlert(
            AlertType.ERROR,
            "Decrypted records not found",
            "Decrypted records not found, please refresh the page.",
          )
          return
        }
        setEncrypted(true)
        if (error?.status === 403) {
          console.error("Decrypt permission denied")
          addAlert(
            AlertType.ERROR,
            "Permission Denied",
            "You do not have permission to decrypt these records.",
            10,
          )
          return setCanDecryptRecords(false)
        }
        // TODO(shakefu): This 405 is a wonky status code.
        if (error?.status === 405) {
          console.error("Decrypt not available on API")
        }
      })
  }, [
    encrypted,
    setEncrypted,
    canDecryptRecords,
    setCanDecryptRecords,
    missingRecords,
    setMissingRecords,
    decryptedRecords,
    setDecryptedRecords,
    pendingRecords,
    setPendingRecords,
  ])

  // Render nothing if we don't have a user
  if (!user) return null
  // Render a not found if we couldn't find a schema in state
  if (!schema) {
    addAlert(AlertType.ERROR, "Schema Not Found", "Schema not found")
  }

  const toggleEncryption = React.useCallback(() => {
    if (!canDecryptRecords && encrypted) {
      console.debug("Can't toggle encryption, can't decrypt")
      return
    }
    console.debug("Toggling encryption")
    setEncrypted(!encrypted)
  }, [encrypted, canDecryptRecords, setEncrypted])

  const fieldNames = React.useMemo(() => {
    if (!recordMap) return []

    if (!hashedSchema || isEmpty(hashedSchema)) {
      // Flatten the recordMap to get the field names and remove duplicates as a set.
      const encryptedKeys = Object.values(recordMap).map((record) =>
        Object.keys(record),
      )
      const flattenedKeys = encryptedKeys.flat()
      const uniqueKeys = new Set(flattenedKeys)
      return Array.from(uniqueKeys)
    } else {
      return Object.keys(hashedSchema || {}).map((key) => {
        if (encrypted) return key
        return hashedSchema[key]?.name
      })
    }
  }, [recordMap, hashedSchema, encrypted])

  // missing here is the idField in the row - JP just can't
  const columns = React.useMemo(() => {
    return fieldNames.map((name) => ({
      accessorKey: name,
      header: name,
      field: name,
      type: properties[name]?.type,
      size: 150,
    }))
  }, [fieldNames, properties])

  const data = React.useMemo(() => {
    return records.map((record) => {
      if (encrypted) {
        return { id: record.id, ...record.data }
      }
      return { id: record.id, ...decryptedRecords[record.id] }
    })
  }, [records, decryptedRecords, encrypted])

  function checkForFilteredRecords(schemaId, count) {
    const recordCounts = SchemaRecordCounts.getValue()
    SchemaRecordCounts.setValue({
      ...recordCounts,
      [schemaId]: count,
    })
  }

  const updateRecords = React.useCallback(async () => {
    if (isLoading) return

    setIsLoading(true)
    try {
      const resp = await api.recordsSearch(
        schemaId,
        currentFilters,
        limit,
        offset,
      )
      setRecords(resp)
      if (
        currentFilters.length === 0 &&
        SchemaRecordCounts.getValue()[schemaId] === undefined
      ) {
        checkForFilteredRecords(schemaId, resp.count)
      }
    } catch (error) {
      addAlert(
        AlertType.ERROR,
        "Error fetching records",
        await unrollError(error),
      )
    } finally {
      setIsLoading(false)
    }
  }, [
    schemaId,
    currentFilters,
    limit,
    offset,
    isLoading,
    setIsLoading,
    setRecords,
  ])

  React.useEffect(() => {
    removeAlertBySource("Error fetching records")
    removeAlertBySource("Decrypted records not found")
    removeAlertBySource("Permission Denied")
    updateRecords()
  }, [currentFilters, limit, offset, schemaId])

  // Remove the pagination dependency from the other effect
  React.useEffect(() => {
    if (isUploading === UploadStatus.COMPLETED) {
      updateRecords()
    }
  }, [isUploading])

  const [isOpen, setIsOpen] = React.useState(false)

  const clearStates = () => {
    if (isUploading === UploadStatus.COMPLETED) {
      updateRecords()
      setUploadProgress(0)
      setIsUploading(UploadStatus.NONE)
      setFileName(null)
    }
  }

  const openModal = () => {
    clearStates()
    setIsOpen(true)
  }
  const closeModal = () => {
    clearStates()
    removeAlertBySource("File Upload")
    clearSelectedFile()
    setIsOpen(false)
  }

  // TODO(jathan): Since this is a component, is it wise to use a callback?
  const EncryptButton = React.useCallback(() => {
    return (
      <>
        <IconLock
          locked="Danger.100"
          unlocked="Success.100"
          encrypted={encrypted}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={toggleEncryption}
          isDisabled={canDecryptRecords === false}
          title={
            canDecryptRecords
              ? "Toggle encryption"
              : "You must be an authorized data owner and decrypt locally using the proxy."
          }
        >
          {encrypted ? "Decrypt" : "Encrypt"}
        </Button>
      </>
    )
  }, [encrypted, canDecryptRecords, toggleEncryption])

  // Toggle encryption on initial mount
  // TODO(jathan): Trigger decryptRecords on load to get proxy overlay to show. Remove this when we have proxy detect endpoint.
  React.useEffect(() => {
    toggleEncryption()
  }, [])

  return (
    <Flex direction="column" flexGrow={1} minHeight={0}>
      <HStack justifyContent="space-between" alignItems="flex-start">
        <Text>{schema.description}</Text>
        <Button
          flexShrink={0}
          variant="primary"
          title="Upload Data to Blind Insight"
          size="md"
          rightIcon={
            isUploading === UploadStatus.UPLOADING ? (
              <CircularProgress size={"1.5em"} value={uploadProgress} />
            ) : (
              <FaUpload />
            )
          }
          onClick={openModal}
        >
          {isUploading === UploadStatus.UPLOADING
            ? "Uploading"
            : "Upload Records"}
        </Button>
      </HStack>
      <Box>
        {hasProxy === false && (
          <Alert width="fit-content" status="info" variant="standard">
            <Text>
              Please{" "}
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                download and run the Blind Proxy
              </Link>{" "}
              to search and decrypt locally. Feel free to get in touch if you
              need assistance.
            </Text>
          </Alert>
        )}
        {hasProxy && <TableFilter />}
      </Box>
      <Flex direction="column" flexGrow={1} minHeight={0}>
        <DataTable
          schemaId={schemaId}
          EncryptButton={EncryptButton}
          hasProxy={hasProxy}
          data={data}
          columns={columns}
          rowCount={count}
          onUpdateRecords={updateRecords}
        />
      </Flex>
      <Modal isOpen={isOpen} onClose={closeModal} variant="dataUpload">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <UploadRecords />
        </ModalContent>
      </Modal>
    </Flex>
  )
}
